import { ref, onMounted } from 'vue';

// Доступные темы
export const THEMES = {
  DEFAULT: 'default',
  DARK: 'dark',
  BLUE: 'blue',
  GREEN: 'green',
  PURPLE: 'purple',
};

// Ключ для localStorage
const THEME_STORAGE_KEY = 'app-theme';

// Реактивное состояние темы (глобальное)
const currentTheme = ref(THEMES.DEFAULT);

/**
 * Composable для управления темами приложения
 * @returns {Object} Объект с методами и свойствами для работы с темами
 */
export function useTheme() {
  /**
   * Устанавливает выбранную тему
   * @param {string} themeName - Название темы из THEMES
   */
  const setTheme = (themeName) => {
    let selectedTheme = themeName;

    if (!Object.values(THEMES).includes(selectedTheme)) {
      console.warn(`Theme "${selectedTheme}" is not available. Using default theme.`);
      selectedTheme = THEMES.DEFAULT;
    }

    currentTheme.value = selectedTheme;

    // Применяем тему к HTML элементу
    document.documentElement.setAttribute('data-theme', selectedTheme);

    // Сохраняем тему в localStorage
    localStorage.setItem(THEME_STORAGE_KEY, selectedTheme);
  };

  /**
   * Получает текущую тему
   * @returns {string} Название текущей темы
   */
  const getTheme = () => currentTheme.value;

  /**
   * Переключает между темами
   * @param {string} theme - Название темы для переключения
   */
  const toggleTheme = (theme) => {
    setTheme(theme);
  };

  /**
   * Инициализирует тему из localStorage или использует default
   */
  const initTheme = () => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);

    if (savedTheme && Object.values(THEMES).includes(savedTheme)) {
      setTheme(savedTheme);
    } else {
      setTheme(THEMES.DEFAULT);
    }
  };

  /**
   * Возвращает список всех доступных тем
   * @returns {Array} Массив объектов с информацией о темах
   */
  const getAvailableThemes = () => [
    { value: THEMES.DEFAULT, label: 'Default', description: 'Light theme with default colors' },
    { value: THEMES.BLUE, label: 'Blue', description: 'Professional blue color scheme' },
    { value: THEMES.GREEN, label: 'Green', description: 'Fresh green color scheme' },
    { value: THEMES.PURPLE, label: 'Purple', description: 'Creative purple color scheme' },
  ];

  /**
   * Проверяет, является ли указанная тема текущей
   * @param {string} theme - Название темы для проверки
   * @returns {boolean} true если тема активна
   */
  const isThemeActive = (theme) => currentTheme.value === theme;

  // Инициализация темы при монтировании
  onMounted(() => {
    initTheme();
  });

  return {
    currentTheme,
    setTheme,
    getTheme,
    toggleTheme,
    initTheme,
    getAvailableThemes,
    isThemeActive,
    THEMES,
  };
}
